import { Body, Icon, IconButton, TextField, Tooltip } from "@indigo-components";
import { FC, useRef, useState } from "react";
import type { QueuedMessage } from "~/pages/indy/useQueuedMessages";
import { annotationCountLabel } from "./annotationSelection";
import "./QueuedMessages.css";

interface QueuedMessagesProps {
  items: QueuedMessage[];
  /** Row in edit mode. Held by the composer so the drain can wait on it. */
  editingId: number | null;
  onEditingChange: (id: number | null) => void;
  onUpdate: (id: number, text: string) => void;
  onRemove: (id: number) => void;
}

/**
 * The stack of messages waiting to send, drawn above the composer surface.
 * One row per message, oldest (next to send) at the top. A row can be edited
 * in place or removed; there is no steer — the running turn is left alone.
 */
export const QueuedMessages: FC<QueuedMessagesProps> = ({
  items,
  editingId,
  onEditingChange,
  onUpdate,
  onRemove,
}) => {
  const [draft, setDraft] = useState("");
  // Escape unmounts the focused editor, and Chrome and Safari fire `blur` on
  // that removal — which would commit the edit Escape just discarded.
  const cancelledRef = useRef(false);

  if (items.length === 0) return null;

  const startEdit = (m: QueuedMessage) => {
    cancelledRef.current = false;
    onEditingChange(m.id);
    setDraft(m.text);
  };
  // A blank save is a removal: an empty message has nothing to send.
  const commitEdit = (id: number) => {
    if (cancelledRef.current) return;
    const trimmed = draft.trim();
    if (trimmed) onUpdate(id, trimmed);
    else onRemove(id);
    onEditingChange(null);
  };
  const cancelEdit = () => {
    cancelledRef.current = true;
    onEditingChange(null);
  };

  return (
    <div className="indy-queued" role="list" aria-label="Queued messages">
      {items.map((m) => {
        const editing = editingId === m.id;
        return (
          <div
            key={m.id}
            role="listitem"
            className={
              editing
                ? "indy-queued__row indy-queued__row--editing"
                : "indy-queued__row"
            }
          >
            <span
              className="indy-queued__glyph"
              title="Queued — sends after Indy replies"
            >
              <Icon name="indent" size="s" fill="var(--indigo-icon-tertiary)" />
            </span>
            {editing ? (
              <TextField
                aria-label="Edit queued message"
                variant="naked"
                size="small"
                areaSize="small"
                value={draft}
                onChange={setDraft}
                isInitiallyFocused
                className="indy-queued__editor"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitEdit(m.id);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    // Escape here only leaves the editor; keep it from the
                    // full-screen page's window-level Esc-to-exit.
                    e.stopPropagation();
                    cancelEdit();
                  }
                }}
                onBlur={() => commitEdit(m.id)}
              />
            ) : (
              <Body
                as="span"
                size="md"
                className="indy-queued__text"
                title={m.text}
              >
                {m.text}
              </Body>
            )}
            {!editing && m.annotations.length > 0 && (
              <Tooltip content="Sends with this message" placement="top">
                <Body as="span" size="xs" className="indy-queued__annotations">
                  <Icon name="comment" size="xs" />
                  {annotationCountLabel(m.annotations.length)}
                </Body>
              </Tooltip>
            )}
            {!editing && (
              <IconButton
                iconName="pencil"
                size="sm"
                tooltip="Edit"
                onClick={() => startEdit(m)}
              />
            )}
            <IconButton
              iconName="trash"
              size="sm"
              tooltip="Remove"
              onClick={() => {
                if (editing) cancelEdit();
                onRemove(m.id);
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
